import React, { useEffect, useState } from "react";
import {
  Badge, Button, Card, Col, Empty, Row, Select, Space,
  Spin, Table, Tag, Typography, Popconfirm, Progress, Tooltip,
} from "antd";
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

const ESTADO_COLOR = {
  Sembrado:       "blue",
  EnCrecimiento:  "green",
  EnFloracion:    "purple",
  ListoCosechar:  "gold",
  Cosechado:      "default",
  Perdido:        "red",
};

const ESTADO_LABEL = {
  Sembrado:      "Sembrado",
  EnCrecimiento: "En crecimiento",
  EnFloracion:   "En floración",
  ListoCosechar: "Listo p/cosechar",
  Cosechado:     "Cosechado",
  Perdido:       "Perdido",
};

export default function FrmCultivoLista() {
  const navigate = useNavigate();
  const [cultivos, setCultivos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroEstado, setFiltroEstado] = useState("todos");

  const cargar = async () => {
    try {
      setCargando(true);
      const resp = await axios.get(`${API_URL}/Cultivo`);
      const lista = Array.isArray(resp.data?.$values)
        ? resp.data.$values
        : Array.isArray(resp.data) ? resp.data : [];
      setCultivos(lista);
    } catch {
      setCultivos([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, []);

  const eliminar = async (id) => {
    try {
      await axios.delete(`${API_URL}/Cultivo/${id}`);
      cargar();
    } catch {
      // silencioso
    }
  };

  const datos = filtroEstado === "todos"
    ? cultivos
    : cultivos.filter(c => c.estado === filtroEstado);

  // ── Tarjetas resumen ──
  const activos   = cultivos.filter(c => !["Cosechado","Perdido"].includes(c.estado)).length;
  const cosechados = cultivos.filter(c => c.estado === "Cosechado").length;
  const costoTotal = cultivos.reduce((s, c) => s + (c.costoTotal ?? 0), 0);
  const pendientes = cultivos.reduce((s, c) => s + (c.actividadesPendientes ?? 0), 0);

  const columns = [
    {
      title: "Cultivo",
      key: "nombre",
      render: (_, r) => (
        <div>
          <Text strong style={{ fontSize: 14 }}>{r.nombre}</Text>
          {r.tipoCultivo && (
            <div><Text type="secondary" style={{ fontSize: 12 }}>{r.tipoCultivo}</Text></div>
          )}
          {r.lote && <Text type="secondary" style={{ fontSize: 12 }}>📍 {r.lote}</Text>}
        </div>
      ),
    },
    {
      title: "Estado",
      dataIndex: "estado",
      key: "estado",
      width: 140,
      render: v => <Tag color={ESTADO_COLOR[v] ?? "default"}>{ESTADO_LABEL[v] ?? v}</Tag>,
    },
    {
      title: "Siembra",
      dataIndex: "fechaSiembra",
      key: "fechaSiembra",
      width: 110,
      render: v => dayjs(v).format("DD/MM/YYYY"),
      sorter: (a, b) => dayjs(a.fechaSiembra).valueOf() - dayjs(b.fechaSiembra).valueOf(),
    },
    {
      title: "Est. cosecha",
      dataIndex: "fechaEstimadaCosecha",
      key: "fechaEstimadaCosecha",
      width: 120,
      render: v => v ? dayjs(v).format("DD/MM/YYYY") : <Text type="secondary">—</Text>,
    },
    {
      title: "Actividades",
      key: "actividades",
      width: 160,
      render: (_, r) => {
        const total = r.totalActividades ?? 0;
        const comp  = r.actividadesCompletadas ?? 0;
        const pct   = total > 0 ? Math.round((comp / total) * 100) : 0;
        return total === 0 ? (
          <Text type="secondary">Sin programar</Text>
        ) : (
          <Tooltip title={`${comp}/${total} completadas, ${r.actividadesPendientes} pendientes`}>
            <div style={{ minWidth: 120 }}>
              <Progress
                percent={pct}
                size="small"
                strokeColor={pct === 100 ? "#52c41a" : "#1677ff"}
                format={() => `${comp}/${total}`}
              />
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: "Costo real",
      dataIndex: "costoTotal",
      key: "costoTotal",
      width: 120,
      align: "right",
      render: v => (
        <Text strong style={{ color: "#52c41a" }}>
          ${Number(v ?? 0).toLocaleString("es-CO")}
        </Text>
      ),
      sorter: (a, b) => (a.costoTotal ?? 0) - (b.costoTotal ?? 0),
    },
    {
      title: "Acciones",
      key: "acciones",
      width: 120,
      align: "center",
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title="Ver detalle y actividades">
            <Button size="small" icon={<EyeOutlined />}
              onClick={() => navigate(`/dashboard/cultivos/${r.id}`)} />
          </Tooltip>
          <Tooltip title="Editar">
            <Button size="small" icon={<EditOutlined />}
              onClick={() => navigate(`/dashboard/cultivos/${r.id}/editar`)} />
          </Tooltip>
          <Popconfirm
            title="¿Eliminar este cultivo?"
            description="Se eliminarán también todas sus actividades."
            onConfirm={() => eliminar(r.id)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>
          🌱 Cultivos
        </Title>
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => navigate("/dashboard/cultivos/nuevo")}>
          Nuevo cultivo
        </Button>
      </div>

      {/* ── Resumen ── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Text type="secondary">Total cultivos</Text>
            <div style={{ fontSize: 26, fontWeight: 700 }}>{cultivos.length}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Text type="secondary">En producción</Text>
            <div style={{ fontSize: 26, fontWeight: 700, color: "#52c41a" }}>{activos}</div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Text type="secondary">Actividades pendientes</Text>
            <div style={{ fontSize: 26, fontWeight: 700, color: pendientes > 0 ? "#fa8c16" : "#8c8c8c" }}>
              {pendientes}
            </div>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Text type="secondary">Costo acumulado</Text>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#1677ff" }}>
              ${costoTotal.toLocaleString("es-CO")}
            </div>
          </Card>
        </Col>
      </Row>

      {/* ── Filtro ── */}
      <Card style={{ marginBottom: 12 }} size="small">
        <Space wrap>
          <Text strong>Filtrar por estado:</Text>
          <Select value={filtroEstado} onChange={setFiltroEstado} style={{ width: 180 }}>
            <Select.Option value="todos">Todos</Select.Option>
            {Object.entries(ESTADO_LABEL).map(([k, v]) => (
              <Select.Option key={k} value={k}>{v}</Select.Option>
            ))}
          </Select>
        </Space>
      </Card>

      <Spin spinning={cargando}>
        {datos.length === 0 && !cargando ? (
          <Empty
            description={
              filtroEstado === "todos"
                ? "No hay cultivos registrados. ¡Crea el primero!"
                : `Sin cultivos con estado "${ESTADO_LABEL[filtroEstado]}".`
            }
          />
        ) : (
          <Table
            rowKey="id"
            columns={columns}
            dataSource={datos}
            pagination={{ pageSize: 15, showSizeChanger: true }}
            scroll={{ x: "max-content" }}
            size="middle"
          />
        )}
      </Spin>
    </div>
  );
}
